import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chat, LikeFill, LikeLine, More } from '@peacecover/icon';
import { useDeleteAdaptedFictionEditionComment } from '../../../domains/scene/ui/hooks/useDeleteAdaptedFictionEditionComment';
import { useLikeAdaptedFictionEditionComment } from '../../../domains/scene/ui/hooks/useLikeAdaptedFictionEditionComment';
import { useInvalidateSceneCommentQueries } from '../hooks/useInvalidateSceneCommentQueries';
import { useFollowUser } from '../../../domains/user/ui/hooks/useFollowUser';
import { useGetUserInfo } from '../../../domains/user/ui/hooks/useGetUserInfo';
import { CastRelationshipBadge } from './CastRelationshipBadge';
import { CommentAuthorAvatar } from './CommentAuthorAvatar';
import { DeleteMenuDropdown } from './DeleteMenuDropdown';
import { ROUTES } from '../../../constants/routes';
import type { components } from '../../../types/schema';

interface CommentDetailReplyItemProps {
  reply: components['schemas']['ApiV1AdaptedFictionEditionComment'];
  adaptedFictionEditionId: string;
  threadCommentId: string;
  adaptedFictionPreviewId?: string;
  sceneId?: string;
}

export const CommentDetailReplyItem = ({
  reply,
  adaptedFictionEditionId,
  threadCommentId,
  adaptedFictionPreviewId,
  sceneId
}: CommentDetailReplyItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const { data: user } = useGetUserInfo();
  const { invalidateReplies, invalidateRepliesAndDetail } = useInvalidateSceneCommentQueries();

  const { mutateAsync: likeAdaptedFictionEditionComment } = useLikeAdaptedFictionEditionComment({
    adaptedFictionEditionId,
    commentId: reply.commentId ?? ''
  });

  const { mutateAsync: deleteAdaptedFictionEditionComment } = useDeleteAdaptedFictionEditionComment(
    {
      adaptedFictionEditionId,
      commentId: reply.commentId ?? ''
    }
  );

  const { mutateAsync: followUser } = useFollowUser();

  const handleLikeAdaptedFictionEditionComment = async () => {
    if (!reply.commentId) return;
    await likeAdaptedFictionEditionComment();
    await invalidateReplies(adaptedFictionEditionId, threadCommentId);
  };

  const handleDeleteAdaptedFictionEditionComment = async () => {
    if (!reply.commentId) return;
    await deleteAdaptedFictionEditionComment();
    await invalidateRepliesAndDetail(adaptedFictionEditionId, threadCommentId);
  };

  const handleFollowUser = async (userProfileId: string) => {
    await followUser({ myProfileId: user?.baseUserProfile?.userProfileId ?? '', userProfileId });
    await invalidateReplies(adaptedFictionEditionId, threadCommentId);
  };

  return (
    <div className="px-[24px] py-[16px] flex items-start gap-[10px]">
      <button
        type="button"
        className="w-[34px] h-[34px]"
        onClick={() => navigate(`/feed/user_profile/${reply.userProfileId}`)}
      >
        <CommentAuthorAvatar
          anonymous={reply.anonymous}
          thumbnailUrl={reply.authorThumbnailUrl ?? undefined}
          size={34}
        />
      </button>

      <div className="flex flex-col items-start w-full">
        <div className="flex items-start justify-between w-full">
          <p className="text-[14px] font-semibold leading-[17px] text-text-main-lv2 mt-[1px] mb-[5px]">
            {reply.authorName}
            <span className="text-[12px] leading-[14px] text-text-main-lv4 ml-[6px]">
              {reply.authoredAt}
            </span>
          </p>
          {!reply.isMine && !reply.isFollowing && (
            <button
              type="button"
              className="text-[12px] font-medium leading-[14px] text-primary-light dark:text-primary-dark"
              onClick={() => handleFollowUser(reply.userProfileId ?? '')}
            >
              {t('SceneCommentsDetail.follow')}
            </button>
          )}
        </div>

        <CastRelationshipBadge castRelationship={reply.castRelationship} className="hidden" />

        <p className="text-[15px] leading-[22px] text-text-main-lv2 mt-[4px] mb-[3px]">
          {reply.content}
        </p>

        <div className="flex items-center justify-between w-full relative">
          <div className="flex items-center gap-[24px] text-text-sub-lv1">
            <button
              type="button"
              className="flex items-center gap-[4px]"
              onClick={() =>
                navigate(
                  `${ROUTES.SCENE.COMMENTS_DETAIL.GO(adaptedFictionEditionId, reply.commentId)}?${sceneId ? `sceneId=${sceneId}` : `adaptedFictionPreviewId=${adaptedFictionPreviewId ?? ''}`}`
                )
              }
              aria-label="대댓글 보기"
            >
              <Chat size={18} />
              <p className="text-[15px] font-medium leading-[18px]">{reply.commentsCount}</p>
            </button>

            <button
              type="button"
              className="flex items-center gap-[4px]"
              onClick={handleLikeAdaptedFictionEditionComment}
              aria-label="좋아요"
            >
              {reply.isLiked ? (
                <LikeFill size={18} className="text-warning" />
              ) : (
                <LikeLine size={18} />
              )}
              <p className="text-[15px] font-medium leading-[18px]">{reply.likesCount}</p>
            </button>
          </div>

          {reply.isMine && (
            <DeleteMenuDropdown
              trigger={<More size={17} className="text-text-main-lv4" />}
              dropdownClassName="absolute bottom-[-39px] right-[27px]"
              onDelete={handleDeleteAdaptedFictionEditionComment}
              stopPropagationOnMouseDown
            />
          )}
        </div>
      </div>
    </div>
  );
};
